import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Grid from "components/common/Grid";
import JarvisButton from "components/JarvisButton";
import BlurWrapper from "components/common/BlurWrapper";
import LockedTableWrapper from "components/common/LockTableWrapper";
import { PHCOnboardingApis } from "stores/PHC/PHCOnboarding/PHCOnboardingApis";
import { PHCOnboardingParser } from "stores/PHC/PHCOnboarding/PHCOnboardingParser";
import { PHCOnboardingActions } from "stores/PHC/PHCOnboarding/PHCOnboardingAction";
import { formatHelper } from "helper/format";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import GpsFixed from "@mui/icons-material/GpsFixed";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import { usePhcTheme } from "../PhcThemeContext";
import {
  DEFAULT_INSIGHT,
  EXPLORE_PRODUCTS,
  INDIA_PRIME_PRODUCT,
  LONG_TERM_PRODUCT,
  SHORT_TERM_PRODUCT,
  US_MULTI_ASSET_PRODUCT,
} from "../recommendedProductsData";

const HORIZON_OPTIONS = [
  { key: "short", label: "Short-Term" },
  { key: "long", label: "Long-Term" },
];

const REGION_OPTIONS = [
  { key: "india", label: "India" },
  { key: "us", label: "US" },
];

const ASSET = {
  optimize: "/images/phc-dashboard/recommend-optimize.png",
  star: "/images/phc-dashboard/recommend-insight-star.png",
  target: "/images/phc-dashboard/recommend-target.svg",
  shield: "/images/phc-dashboard/recommend-shield.svg",
  headset: "/images/phc-dashboard/recommend-headset.svg",
  exploreArrow: "/images/phc-dashboard/explore-arrow.svg",
};

/** Explore / RM cards stay dark in both Light and Dark PHC themes. */
const DARK_CARD = {
  bg: "#00010D",
  footerBg: "#010D1B",
  footerBgMobile: "#00010D",
  border: "#3C4D62",
  text: "#FFFFFF",
  muted: "rgba(255,255,255,0.72)",
};

const TermToggle = ({
  value,
  onChange,
  options = HORIZON_OPTIONS,
  fullWidth = false,
}) => {
  const { theme } = usePhcTheme();

  return (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      p: "4px",
      borderRadius: "999px",
      bgcolor: theme.termToggleTrack,
      border: theme.termToggleBorder,
      flexShrink: 0,
      width: fullWidth ? "100%" : "auto",
    }}
  >
    {options.map(({ key, label }) => {
      const active = value === key;
      return (
        <Box
          key={key}
          component="button"
          type="button"
          onClick={() => onChange(key)}
          sx={{
            border: 0,
            cursor: "pointer",
            px: { xs: 1, md: 2.25 },
            py: 1,
            borderRadius: "999px",
            bgcolor: active ? theme.termToggleActiveBg : "transparent",
            color: active ? theme.termToggleActiveColor : theme.termToggleIdleColor,
            fontWeight: 600,
            fontSize: { xs: 12, md: 14 },
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            flex: fullWidth ? 1 : "0 0 auto",
            minWidth: 0,
          }}
        >
          {label}
        </Box>
      );
    })}
  </Box>
  );
};

const REVIEW_DIMENSIONS = [
  {
    key: "protection",
    altKeys: [],
    fallbackTitle: "Protection",
    accent: "#2977EC",
    tint: "rgba(41,119,236,0.10)",
    Icon: ShieldOutlined,
  },
  {
    key: "optimization",
    altKeys: ["optimisation"],
    fallbackTitle: "Optimization",
    accent: "#F6A823",
    tint: "rgba(246,168,35,0.14)",
    Icon: GpsFixed,
  },
  {
    key: "personalization",
    altKeys: ["personalisation"],
    fallbackTitle: "Personalization",
    accent: "#7C3AED",
    tint: "rgba(124,58,237,0.10)",
    Icon: AutoAwesome,
  },
];

const extractReviewItems = (review) => {
  if (!review || typeof review !== "object") return [];
  return REVIEW_DIMENSIONS.map((dim) => {
    const source =
      review[dim.key] ||
      dim.altKeys.reduce((found, alt) => found || review[alt], null);
    if (!source) return null;
    const summary = source.summary || "";
    const content = source.content || "";
    if (!summary && !content) return null;
    return {
      key: dim.key,
      title: source.title || dim.fallbackTitle,
      summary,
      content,
      accent: dim.accent,
      tint: dim.tint,
      Icon: dim.Icon,
    };
  }).filter(Boolean);
};

const MarkdownBold = ({ text, sx }) => {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return (
    <Text sx={sx}>
      {parts.map((part, index) => {
        if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
          return (
            <Box key={`${part}-${index}`} component="strong" sx={{ fontWeight: 700 }}>
              {part.slice(2, -2)}
            </Box>
          );
        }
        return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
      })}
    </Text>
  );
};

const ReviewCard = ({ item, compact = false }) => {
  const { theme } = usePhcTheme();
  const [expanded, setExpanded] = useState(!compact);
  const longContent = (item.content || "").length > 220;
  const showToggle = compact && longContent;
  const showContent = item.content && (!showToggle || expanded);
  const Icon = item.Icon;

  return (
    <Box
      sx={{
        height: "100%",
        borderRadius: "16px",
        border: `1px solid ${theme.sectionBorder}`,
        backgroundColor: theme.cardBg,
        p: { xs: 2, md: 2.5 },
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          alignSelf: "flex-start",
          px: 1.25,
          py: 0.5,
          borderRadius: "999px",
          bgcolor: item.tint,
        }}
      >
        {Icon ? (
          <Icon sx={{ fontSize: 16, color: item.accent }} />
        ) : null}
        <Text
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: item.accent,
            letterSpacing: "0.02em",
          }}
        >
          {item.title}
        </Text>
      </Box>
      {item.summary ? (
        <MarkdownBold
          text={item.summary}
          sx={{
            fontSize: { xs: 15, md: 16 },
            fontWeight: 500,
            color: theme.textPrimary,
            lineHeight: 1.5,
          }}
        />
      ) : null}
      {showContent ? (
        <MarkdownBold
          text={item.content}
          sx={{
            fontSize: { xs: 13, md: 14 },
            color: theme.textSecondary,
            lineHeight: 1.55,
          }}
        />
      ) : null}
      {showToggle ? (
        <Box
          component="button"
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            alignSelf: "flex-start",
            border: 0,
            bgcolor: "transparent",
            cursor: "pointer",
            p: 0,
            mt: 0.25,
            fontWeight: 700,
            fontSize: 13,
            color: item.accent,
          }}
        >
          {expanded ? "Show less" : "See full insight"}
        </Box>
      ) : null}
    </Box>
  );
};

const PortfolioReviewIntro = ({ review, componentState }) => {
  const { theme } = usePhcTheme();
  const items = useMemo(() => extractReviewItems(review), [review]);
  const [activeKey, setActiveKey] = useState(items[0]?.key || "protection");

  useEffect(() => {
    if (!items.some((item) => item.key === activeKey)) {
      setActiveKey(items[0]?.key || "protection");
    }
  }, [items, activeKey]);

  if (items.length === 0) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
        <Text
          sx={{
            fontWeight: 700,
            fontSize: { xs: 22, md: 28 },
            color: theme.textPrimary,
            textAlign: "center",
            letterSpacing: "-0.02em",
          }}
        >
          What this portfolio is telling you?
        </Text>
        <Box
          sx={{
            width: "100%",
            borderRadius: "16px",
            border: `1px solid ${theme.sectionBorder}`,
            backgroundColor: theme.cardBg,
            px: { xs: 2, md: 3 },
            py: { xs: 2.5, md: 3 },
            textAlign: "center",
          }}
        >
          <Text
            sx={{
              fontSize: { xs: 13, md: 14 },
              color: theme.textSecondary,
              lineHeight: 1.55,
            }}
          >
            Insights are currently unavailable for this portfolio.
          </Text>
        </Box>
      </Stack>
    );
  }

  const activeItem = items.find((item) => item.key === activeKey) || items[0];
  const toggleOptions = items.map((item) => ({
    key: item.key,
    label: item.title,
  }));
  const shouldShowOverlay =
    componentState === "blur" || componentState === "locked";
  const OverlayComponent =
    componentState === "blur" ? BlurWrapper : LockedTableWrapper;

  return (
    <Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
      <Text
        sx={{
          fontWeight: 700,
          fontSize: { xs: 22, md: 28 },
          color: theme.textPrimary,
          textAlign: "center",
          letterSpacing: "-0.02em",
        }}
      >
        What this portfolio is telling you?
      </Text>
      <Text
        sx={{
          fontSize: { xs: 14, md: 16 },
          color: theme.textSecondary,
          textAlign: "center",
          lineHeight: 1.5,
          maxWidth: 820,
        }}
      >
        Jarvis AI has analysed your portfolio across 3 dimensions: protection,
        optimisation, and personalisation.
      </Text>

      <Box sx={{ position: "relative", width: "100%", isolation: "isolate" }}>
        <Box
          sx={{
            position: "relative",
            zIndex: 0,
            filter: shouldShowOverlay ? "blur(4px)" : "none",
            transition: "filter 0.2s ease",
            pointerEvents: shouldShowOverlay ? "none" : "auto",
            userSelect: shouldShowOverlay ? "none" : "auto",
          }}
        >
          <Box sx={{ display: { xs: "block", md: "none" }, width: "100%" }}>
            <Stack spacing={1.5}>
              <TermToggle
                value={activeKey}
                onChange={setActiveKey}
                options={toggleOptions}
                fullWidth
              />
              {activeItem ? (
                <ReviewCard key={activeItem.key} item={activeItem} compact />
              ) : null}
            </Stack>
          </Box>
          <Grid
            container
            spacing={1.5}
            sx={{ display: { xs: "none", md: "flex" }, width: "100%" }}
          >
            {items.map((item) => (
              <Grid item xs={12} md={4} key={item.key}>
                <ReviewCard item={item} />
              </Grid>
            ))}
          </Grid>
        </Box>
        {shouldShowOverlay ? (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "148px", md: "250px" },
              maxWidth: "100%",
              height: "auto",
              zIndex: 10,
              pointerEvents: "auto",
            }}
          >
            <OverlayComponent
              blur={componentState === "blur"}
              locked={componentState === "locked"}
            />
          </Box>
        ) : null}
      </Box>
    </Stack>
  );
};

const BulletRow = ({ text, color }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <Box
      sx={{
        width: 0,
        height: 0,
        mt: "6px",
        borderLeft: "6px solid transparent",
        borderRight: "6px solid transparent",
        borderBottom: `9px solid ${color}`,
        transform: "rotate(90deg)",
        flexShrink: 0,
      }}
    />
    <Text
      sx={{
        fontSize: { xs: 13, md: 14 },
        color: "#1A1A1A",
        lineHeight: 1.45,
        fontWeight: 500,
      }}
    >
      {text}
    </Text>
  </Stack>
);

const CtaButton = ({ label, bg, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      width: "100%",
      border: 0,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      borderRadius: "14px",
      py: 1.5,
      px: 2,
      background: bg,
      color: "#FFFFFF",
      fontWeight: 700,
      fontSize: { xs: 14, md: 16 },
      boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
      "&:hover": { filter: "brightness(1.05)" },
    }}
  >
    {label}
    <Box
      component="img"
      src={ASSET.exploreArrow}
      alt=""
      sx={{ width: 18, height: 18, flexShrink: 0 }}
    />
  </Box>
);

const OptimizePanel = ({ recommendation, sellValue, onViewSell, componentState }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const source = useSelector((state) => state.common.source);
  const naviaSignupUrl = useSelector(
    (state) => state.PHC?.PHCOnboarding?.redirectUrl,
  );
  const hideSellValue =
    componentState === "blur" || componentState === "locked";

  const handleUnlock = async () => {
    try {
      if (componentState === "blur") {
        if (naviaSignupUrl) {
          window.location.href = naviaSignupUrl;
        }
        return;
      }

      if (source === "navia") {
        history.push({
          pathname: "/re-kyc",
          state: { from: "phcIframe" },
        });
        return;
      }

      const res = await PHCOnboardingApis.redirectToCkyc();
      if (res?.code == 200) {
        const onboardingResponse = await PHCOnboardingApis.getOnboardingData();
        const parsedOnboardingData =
          PHCOnboardingParser.onboardingData(onboardingResponse);
        dispatch(
          PHCOnboardingActions.updatePHCOnboardingData(parsedOnboardingData),
        );
      }
    } catch (error) {
      console.error("CKYC redirect failed", error);
    }
  };

  return (
  <Box
    sx={{
      height: "100%",
      bgcolor: "#FFFFFF",
      borderRadius: { xs: "20px", md: "20px 0 0 20px" },
      p: { xs: 2.5, md: 3 },
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        alignSelf: "flex-start",
        px: 1.25,
        py: 0.75,
        borderRadius: "999px",
        bgcolor: "#E8F1FF",
      }}
    >
      <Box
        component="img"
        src={ASSET.star}
        alt=""
        sx={{ width: 16, height: 16, objectFit: "contain" }}
      />
      <Text
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: "#3B82F6",
          letterSpacing: "0.04em",
        }}
      >
        JARVIS AI KEY INSIGHT
      </Text>
    </Box>

    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Text
          sx={{
            fontWeight: 800,
            fontSize: { xs: 22, md: 28 },
            color: "#0A0A0A",
            lineHeight: 1.2,
            mb: 1,
          }}
        >
          Optimize Your Portfolio
        </Text>
        <Text sx={{ fontSize: 14, color: "#667085", lineHeight: 1.45 }}>
          Our analysis indicates that the amount below can be invested optimally.
        </Text>
      </Box>
      <Box
        component="img"
        src={ASSET.optimize}
        alt=""
        sx={{
          width: { xs: 120, md: 150 },
          height: "auto",
          objectFit: "contain",
          flexShrink: 0,
        }}
      />
    </Stack>

    <Box
      sx={{
        border: "1px solid rgba(220,38,38,0.35)",
        bgcolor: "rgba(254,226,226,0.35)",
        borderRadius: "14px",
        p: { xs: 1.5, md: 2 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
      >
        <Box sx={{ minWidth: 0 }}>
          <Text
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "#DC2626",
              letterSpacing: "0.04em",
              mb: 0.5,
            }}
          >
            ESTIMATED SELL CALLS
          </Text>
          <Text
            sx={{
              fontSize: { xs: 22, md: 28 },
              fontWeight: 800,
              color: "#DC2626",
              lineHeight: 1.1,
              filter: hideSellValue ? "blur(6px)" : "none",
              userSelect: hideSellValue ? "none" : "auto",
              width: "fit-content",
            }}
          >
            {sellValue}
          </Text>
        </Box>
        {hideSellValue ? (
          <JarvisButton
            disableEndIcon
            disabled={componentState === "blur" && !naviaSignupUrl}
            onClick={handleUnlock}
            sx={{
              flexShrink: 0,
              width: "auto",
              minHeight: 36,
              px: 2,
              textTransform: "none",
            }}
          >
            {componentState === "blur" ? "Sign in" : "Unlock"}
          </JarvisButton>
        ) : null}
      </Stack>
    </Box>

    <Text
      sx={{
        mt: "auto",
        fontSize: 11,
        lineHeight: 1.5,
        color: "#9CA3AF",
        fontWeight: 400,
      }}
    >
      <Box component="span" sx={{ fontWeight: 700, color: "#6B7280" }}>
        Disclaimer:
      </Box>{" "}
      Securities flagged for sell are identified using a combination of
      fundamental and technical parameters and are analytical outputs only, not
      investment advice or a recommendation. Investments in securities markets
      are subject to market risks. Users are advised to exercise their own
      discretion and to consult Jarvis Invest Private Limited, a SEBI-registered
      Investment Adviser (INA000013235), before making any investment decision.
    </Text>

    {/* <Box sx={{ mt: "auto" }}>
      <CtaButton
        label="View Stocks in Sell"
        bg="linear-gradient(90deg, #1D4ED8 0%, #2563EB 100%)"
        onClick={onViewSell}
      />
    </Box> */}
  </Box>
  );
};

const ProductPanel = ({ config, onStart }) => (
  <Box
    sx={{
      height: "100%",
      background: config.panelBg,
      borderRadius: { xs: "20px", md: "0 20px 20px 0" },
      p: { xs: 2.5, md: 3 },
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <Box
      sx={{
        display: "inline-flex",
        alignSelf: "flex-start",
        px: 1.5,
        py: 0.75,
        borderRadius: "999px",
        background: config.badgeBg,
      }}
    >
      <Text sx={{ fontSize: 12, fontWeight: 600, color: "#FFFFFF" }}>
        {config.badge}
      </Text>
    </Box>

    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "flex-start" }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Text
          sx={{
            fontWeight: 800,
            fontSize: { xs: 22, md: 28 },
            lineHeight: 1.15,
            mb: 0.5,
          }}
        >
          <Box component="span" sx={{ color: "#0A0A0A" }}>
            {config.titleJarvis}{" "}
          </Box>
          <Box component="span" sx={{ color: config.titleColor }}>
            {config.titleProduct}
          </Box>
        </Text>
        <Text sx={{ fontSize: 15, fontWeight: 600, color: "#111827", mb: 1.5 }}>
          {config.subtitle}
        </Text>
        <Text sx={{ fontSize: 14, fontWeight: 700, color: "#111827", mb: 1 }}>
          {config.listTitle}
        </Text>
        <Stack spacing={1}>
          {config.bullets.map((item) => (
            <BulletRow key={item} text={item} color={config.bulletColor} />
          ))}
        </Stack>
      </Box>
      <Box
        component="img"
        src={config.image}
        alt=""
        sx={{
          width: { xs: 220, sm: 260, md: 320 },
          height: "230px",
          objectFit: "contain",
          flexShrink: 0,
          alignSelf: { xs: "center", sm: "center" },
        }}
      />
    </Stack>

    <Box
      sx={{
        border: `1px solid ${config.rangeBorder}`,
        borderRadius: "12px",
        px: 2,
        py: 1.5,
        bgcolor: config.rangeBg || "rgba(255,255,255,0.55)",
      }}
    >
      <Text
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: config.titleColor,
          mb: 0.5,
        }}
      >
        {config.rangeTitle}
      </Text>
      <Text sx={{ fontSize: 13, color: "#1F2937", lineHeight: 1.45 }}>
        {config.rangeText}
      </Text>
    </Box>

    <Box sx={{ mt: "auto" }}>
      <CtaButton label={config.cta} bg={config.buttonBg} onClick={onStart} />
    </Box>
  </Box>
);

const ExploreCard = ({ product, onStart }) => {
  const layout = product.imageLayout || {};

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: 398, md: 398 },
        borderRadius: "16px",
        border: `1px solid ${product.border || "#9EC5FF"}`,
        bgcolor: product.cardBg || DARK_CARD.bg,
        overflow: "hidden",
        backdropFilter: "blur(6px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Hero art — Figma mobile 5910:2744 / desktop placement */}
      <Box
        component="img"
        src={product.image}
        alt=""
        sx={{
          position: "absolute",
          left: layout.left ?? { xs: 160, md: "auto" },
          right: layout.right ?? "auto",
          top: layout.top ?? { xs: 80, md: 40 },
          width: layout.size ?? { xs: 220, md: 332 },
          height: layout.size ?? "auto",
          maxHeight: { xs: 260, md: "78%" },
          objectFit: "contain",
          objectPosition: "right center",
          opacity: layout.opacity ?? 0.9,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          px: "19px",
          pt: "24px",
          pb: "25px",
        }}
      >
        <Text
          sx={{
            fontWeight: 700,
            fontSize: { xs: 22, md: 24 },
            lineHeight: { xs: "30px", md: "32px" },
          }}
        >
          <Box component="span" sx={{ color: DARK_CARD.text }}>
            {product.titleWhite}
            {product.titleAccent ? " " : ""}
          </Box>
          {!!product.titleAccent && (
            <Box component="span" sx={{ color: product.accent }}>
              {product.titleAccent}
            </Box>
          )}
        </Text>
        <Box
          component="img"
          src={product.underline}
          alt=""
          sx={{
            display: "block",
            width: { xs: 168, md: 205 },
            height: { xs: 6, md: 7 },
            mt: "4px",
            mb: 1.5,
          }}
        />

        <Text
          sx={{
            fontWeight: 500,
            fontSize: { xs: 14, md: 18 },
            color: product.accent,
            mb: 1,
            lineHeight: "normal",
            maxWidth: product.contentWidth || { md: 320 },
          }}
        >
          What you Unlock
        </Text>
        <Stack
          spacing="8px"
          sx={{
            width: "100%",
            maxWidth: product.contentWidth || { md: 320 },
            mb: { xs: 2, md: "auto" },
          }}
        >
          {product.bullets.map((b) => (
            <Stack
              key={b}
              direction="row"
              spacing="12px"
              alignItems="flex-start"
            >
              <Box
                component="img"
                src={product.checkIcon}
                alt=""
                sx={{
                  width: 20,
                  height: 20,
                  flexShrink: 0,
                  mt: "2px",
                }}
              />
              <Text
                sx={{
                  fontSize: 12,
                  color: DARK_CARD.text,
                  lineHeight: { xs: "18px", md: "20px" },
                  textTransform: "capitalize",
                  fontWeight: 400,
                }}
              >
                {b}
              </Text>
            </Stack>
          ))}
        </Stack>

        <Box
          component="button"
          type="button"
          onClick={onStart}
          sx={{
            position: "relative",
            width: "100%",
            height: 56,
            mt: { xs: 1.5, md: 2.5 },
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "14px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            overflow: "hidden",
            px: 2,
            background: product.buttonBg,
            color: "#FFFFFF",
            fontWeight: 800,
            fontSize: { xs: 13, md: 16 },
            lineHeight: "24px",
            whiteSpace: "nowrap",
            boxShadow:
              "0px 10px 24px -12px rgba(124,58,237,0.2), 0px 14px 28px -10px rgba(0,0,0,0.4)",
            textShadow: "0px 1px 2px rgba(0,0,0,0.15)",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              borderRadius: "inherit",
              pointerEvents: "none",
              boxShadow: "inset 0px 1px 2px 0px rgba(255,255,255,0.33)",
            },
            "&:hover": { filter: "brightness(1.06)" },
          }}
        >
          {product.buttonLabel}
          <Box
            component="img"
            src={ASSET.exploreArrow}
            alt=""
            sx={{ width: 18, height: 18, flexShrink: 0 }}
          />
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Recommended Product CTA block — Figma 5895:3624
 * Placed above Ratio Glossary.
 */
const RecommendedProductsSection = ({
  portfolioValue,
  recommendation,
  estimatedSellCalls,
  isAbove25Lac = false,
  review,
  onViewSell,
  onSpeakToRm,
  componentState,
}) => {
  const { theme } = usePhcTheme();
  const history = useHistory();
  const above25Lac = Boolean(isAbove25Lac);
  const [selection, setSelection] = useState(above25Lac ? "india" : "long");

  useEffect(() => {
    setSelection(above25Lac ? "india" : "long");
  }, [above25Lac]);

  const toggleOptions = above25Lac ? REGION_OPTIONS : HORIZON_OPTIONS;

  const productConfig = above25Lac
    ? selection === "us"
      ? US_MULTI_ASSET_PRODUCT
      : INDIA_PRIME_PRODUCT
    : selection === "short"
      ? SHORT_TERM_PRODUCT
      : LONG_TERM_PRODUCT;

  const exploreProducts = useMemo(() => {
    if (!above25Lac) return EXPLORE_PRODUCTS;
    const hideKey = selection === "us" ? "us" : "prime";
    return EXPLORE_PRODUCTS.filter((product) => product.key !== hideKey);
  }, [above25Lac, selection]);

  const sellDisplay = useMemo(() => {
    if (estimatedSellCalls != null && estimatedSellCalls !== "") {
      if (typeof estimatedSellCalls === "number") {
        return formatHelper.currency(estimatedSellCalls, {
          config: { maximumFractionDigits: 0 },
        });
      }
      return String(estimatedSellCalls);
    }
    return DEFAULT_INSIGHT.estimatedSellCalls;
  }, [estimatedSellCalls]);

  const go = (path) => {
    if (path) history.push(path);
  };

  return (
    <Stack spacing={{ xs: "22px", md: 4 }} sx={{ width: "100%" }}>
      <PortfolioReviewIntro review={review} componentState={componentState} />

      {/* Recommended Product */}
      <Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: { xs: "13px", md: 2.5 } }}
        >
          <Text
            sx={{
              fontWeight: { xs: 500, md: 700 },
              fontSize: { xs: 20, md: 28 },
              lineHeight: { xs: "normal", md: "normal" },
              color: theme.textPrimary,
              letterSpacing: "-0.02em",
              maxWidth: { xs: 148, md: "none" },
            }}
          >
            Recommended Product
          </Text>
          <Box sx={{ alignSelf: { xs: "flex-start", sm: "auto" } }}>
            <TermToggle
              value={selection}
              onChange={setSelection}
              options={toggleOptions}
            />
          </Box>
        </Stack>

        <Box
          sx={{
            borderRadius: { xs: "30px", md: "24px" },
            overflow: "hidden",
            bgcolor: "#FFFFFF",
            border: { xs: "1px solid #000000", md: "none" },
            boxShadow: { xs: "none", md: "0 12px 40px rgba(0,0,0,0.25)" },
          }}
        >
          <Grid container>
            <Grid item xs={12} md={6}>
              <OptimizePanel
                recommendation={
                  recommendation || DEFAULT_INSIGHT.recommendation
                }
                sellValue={sellDisplay}
                componentState={componentState}
                onViewSell={
                  onViewSell || (() => go("/jarvis-portfolio/action-centre"))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProductPanel
                key={selection}
                config={productConfig}
                onStart={() => go(productConfig.href)}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box>
        <Text
          sx={{
            fontWeight: 500,
            fontSize: { xs: 24, md: 28 },
            lineHeight: { xs: "32px", md: 1.2 },
            color: theme.textPrimary,
            mb: { xs: 3, md: 2.5 },
          }}
        >
          Explore more Products
        </Text>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            flexWrap: "wrap",
            gap: "22px",
          }}
        >
          {exploreProducts.map((product) => (
            <Box
              key={product.key}
              sx={{
                display: "flex",
                width: { xs: "100%", md: "calc((100% - 44px) / 3)" },
                flex: { md: "0 0 auto" },
              }}
            >
              <ExploreCard
                product={product}
                onStart={() => go(product.href)}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* RM footer — Figma 5895:3745 (desktop) / 5910:2849 (mobile) */}
      <Box
        sx={{
          borderRadius: "16px",
          border: `1px solid ${DARK_CARD.border}`,
          backgroundColor: {
            xs: DARK_CARD.footerBgMobile,
            md: DARK_CARD.footerBg,
          },
          backdropFilter: { xs: "none", md: "blur(6px)" },
          WebkitBackdropFilter: { xs: "none", md: "blur(6px)" },
          px: "21px",
          py: { xs: "21px", md: "17px" },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "center", md: "center" },
          justifyContent: "space-between",
          gap: { xs: 3.5, md: "12px" },
          textAlign: { xs: "center", md: "left" },
        }}
      >
        <Stack
          spacing={{ xs: 2, md: "12px" }}
          direction={{ xs: "column", md: "row" }}
          alignItems="center"
          sx={{ flex: 1, width: "100%", minWidth: 0 }}
        >
          <Box
            component="img"
            src={ASSET.shield}
            alt=""
            sx={{
              width: { xs: 119, md: 40 },
              height: { xs: 119, md: 40 },
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: { xs: "10px", md: "4px" },
            }}
          >
            <Text
              sx={{
                fontSize: { xs: 16, md: 24 },
                fontWeight: 500,
                color: DARK_CARD.text,
                lineHeight: "normal",
              }}
            >
              Jarvis helps you stay invested, diversified &amp; disciplined.
            </Text>
            <Text
              sx={{
                fontSize: { xs: 14, md: 18 },
                fontWeight: 400,
                color: DARK_CARD.muted,
                lineHeight: "normal",
                display: "block",
              }}
            >
              Smart rebalancing today, Stronger portfolio tomorrow.
            </Text>
          </Box>
        </Stack>
        <Box
          component="button"
          type="button"
          onClick={onSpeakToRm}
          sx={{
            cursor: onSpeakToRm ? "pointer" : "default",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: { xs: "100%", md: "auto" },
            minWidth: { md: 247 },
            minHeight: { xs: 64, md: "auto" },
            px: "20px",
            py: "16px",
            borderRadius: "12px",
            border: "1px solid #0D6BF5",
            backgroundImage:
              "linear-gradient(270deg, rgba(13,107,245,0.5) 0%, rgba(8,62,143,0.1) 100%)",
            bgcolor: "transparent",
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: 16,
            lineHeight: "24px",
            whiteSpace: "nowrap",
            flexShrink: 0,
            "&:hover": { filter: onSpeakToRm ? "brightness(1.08)" : "none" },
          }}
        >
          <Box
            component="img"
            src={ASSET.headset}
            alt=""
            sx={{
              width: 32,
              height: 32,
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
          Speak to Jarvis RM
        </Box>
      </Box>
    </Stack>
  );
};

export default RecommendedProductsSection;
